import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Textarea } from '@monksflow/monks-ui';
import { FormSheet } from '@/shared/ui/FormSheet';

/**
 * Ceiling on the field. Generous next to the rules this is for ("always British English",
 * "never say X"), and the server truncates well above it, so the cap bites only on a paste
 * nobody meant to make.
 */
export const MAX_CUSTOM_INSTRUCTIONS = 2000;

export interface CustomInstructionsSheetProps {
  /** The template's saved instructions. Seeds the draft once, on mount. */
  initialValue: string;
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (complementaryPrompt: string) => void;
}

/**
 * Per-template standing instructions for the agent.
 *
 * Mount it only while it should be open: the draft seeds from `initialValue` once, so unmounting
 * is what discards an abandoned edit — the same contract `TemplateNameSheet` keeps.
 *
 * Saving persists on its own, rather than joining the editor's HTML draft. That draft is guarded
 * by an unsaved-changes blocker keyed to the markup alone, and folding a second field into it
 * would make leaving the page interrupt over a form the user already saved.
 */
export function CustomInstructionsSheet({
  initialValue,
  isSaving = false,
  onCancel,
  onSubmit,
}: CustomInstructionsSheetProps) {
  const { t } = useTranslation('admin');
  const [value, setValue] = useState(initialValue);

  const trimmed = value.trim();

  return (
    <FormSheet
      open
      onOpenChange={(next) => !next && !isSaving && onCancel()}
      title={t('templatesConfig.customInstructions.title')}
      description={t('templatesConfig.customInstructions.description')}
    >
      <FormSheet.Body className="flex flex-col gap-1.5">
        <Label htmlFor="template-custom-instructions">
          {t('templatesConfig.customInstructions.label')}
        </Label>
        {/* `max-h-64`, not `rows`: Textarea ships `field-sizing-content`, which overrides `rows`
            and grows the field to fit its value. A max height is the only thing that bounds it. */}
        <Textarea
          id="template-custom-instructions"
          value={value}
          // Clamped here rather than via the native `maxLength`: that attribute rejects EVERY
          // insertion once the value is at or over the cap, so a template whose stored value already
          // exceeds it (set through the admin tool-config form, which enforces no cap) could not be
          // edited in place at all — not even mid-string. Clamping keeps it editable and lets the
          // counter tell the truth about an over-cap value.
          onChange={(event) => setValue(event.target.value.slice(0, MAX_CUSTOM_INSTRUCTIONS))}
          placeholder={t('templatesConfig.customInstructions.placeholder')}
          disabled={isSaving}
          className="max-h-64 min-h-32"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- sole field of a sheet opened to type into
          autoFocus
        />
        <div className="flex items-start justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {t('templatesConfig.customInstructions.help')}
          </p>
          <p className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {t('templatesConfig.customInstructions.counter', {
              count: value.length,
              max: MAX_CUSTOM_INSTRUCTIONS,
            })}
          </p>
        </div>
      </FormSheet.Body>
      <FormSheet.Footer
        secondary={{ label: t('templatesConfig.customInstructions.cancel'), onClick: onCancel }}
        primary={{
          label: t('templatesConfig.customInstructions.save'),
          onClick: () => onSubmit(trimmed),
          loading: isSaving,
          disabled: trimmed === initialValue.trim(),
        }}
      />
    </FormSheet>
  );
}

CustomInstructionsSheet.displayName = 'CustomInstructionsSheet';
